import React, { createRef, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/styles'
import { useParams, useSearchParams } from 'react-router-dom'
import useVenuesApi from 'firebaseKit/venues'

const formatTimestamp = (ts) => {
  return (new Date(ts._seconds * 1000)).toLocaleString('en-GB')
}

const LeaderboardIndex = styled('span')(() => ({
  fontSize: '1.5em',
  paddingRight: '1rem',
  width: '3rem'
}))

const LeaderboardPlayerName = styled('span')(() => ({
  flexGrow: 1,
  fontSize: '1.5em'
}))

const LeaderboardScore = styled('span')(() => ({
  fontSize: '1.5em',
  paddingLeft: '1rem'
}))

const LeaderboardGameAndTime = (props) => {
  let content = ''
  if (props.gameName) {
    content = `"${props.gameName}"`
  }
  if (props.scoreTime) {
    content = `${content} played at ${formatTimestamp(props.scoreTime)}`
  }
  return (
    <span
      style={{
        color: '#404040',
        fontSize: '0.75em'
      }}
    >
      {content}
    </span>
  )
}

const LeaderboardEntry = (props) => {
  let fontColor = 'black'
  let fontSize = '1rem'
  let fontWeight = 'normal'
  if (props.gsb) {
    switch (props.index) {
      case 0:
        fontColor = 'gold'
        fontSize = '2rem'
        fontWeight = 'bold'
        break
      case 1:
        fontColor = 'silver'
        fontSize = '1.5rem'
        break
      case 2:
        fontColor = 'brown'
        fontSize = '1.25rem'
        break
    }
  }

  return (
    <li
      style={{
        alignItems: 'top',
        color: fontColor,
        display: 'flex',
        flexDirection: 'row',
        fontSize,
        fontWeight,
        margin: '1rem 1rem'
      }}
    >
      <LeaderboardIndex>{props.index + 1}</LeaderboardIndex>
      {props.children}
    </li>
  )
}

const LeaderboardPage = (_props) => {
  const params = useParams()
  const [searchParams, _setSearchParams] = useSearchParams()

  // Variables affecting display format
  const [displayMode, setDisplayMode] = useState('WEB')
  const [gsb, setGsb] = useState(true)
  const [oneline, setOneline] = useState(false)
  const [showVenueLogo, setShowVenueLogo] = useState(true)

  const [venueData, setVenueData] = useState(null)
  const [leaderboard, setLeaderboard] = useState(null)
  const [currentPlayerName, setCurrentPlayerName] = useState(null)
  const [currentPlayerScore, setCurrentPlayerScore] = useState(null)
  const [initialScroll, setInitialScroll] = useState(false)
  const entryRefs = useRef([])
  const { getLeaderboardForVenue, getVenue } = useVenuesApi()

  const TV_MODE_WINDOW_SIZE = 8
  const TV_MODE_UPDATE_INTERVAL = 10000
  const [displayWindow, setDisplayWindow] = useState([0, TV_MODE_WINDOW_SIZE])
  let displayWindowUpdateTimeout = null
  const [leaderboardDisplayEntries, setLeaderboardDisplayEntries] = useState(null)

  const scrollToEntry = () => {
    if (initialScroll) {
      return
    }
    if (!leaderboard?.result || leaderboard.result.length === 0) {
      return
    }
    const index = leaderboard.result
      .map((v, i) => ({ i, v }))
      .filter((v) => v.v.player_name === currentPlayerName && Math.round(v.v.score) === currentPlayerScore)
      .map((v) => v.i)
    if (index.length > 0) {
      const i = index[0]
      if (entryRefs.current[i]?.current) {
        entryRefs.current[i].current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
        setInitialScroll(true)
      }
    }
  }

  const resetDisplayWindow = () => {
    const newDisplayWindow = [0, TV_MODE_WINDOW_SIZE]
    setDisplayWindow(newDisplayWindow)
    return newDisplayWindow
  }

  const updateDisplayWindow = (entries, currentDisplayWindow) => {
    let newDisplayWindow = currentDisplayWindow.map((x) => x + TV_MODE_WINDOW_SIZE)
    if (entries && newDisplayWindow[0] >= entries.length) {
      newDisplayWindow = [0, TV_MODE_WINDOW_SIZE]
    }
    setDisplayWindow(newDisplayWindow)
    displayWindowUpdateTimeout = setTimeout(
      () => updateDisplayWindow(entries, newDisplayWindow),
      TV_MODE_UPDATE_INTERVAL
    )
  }

  const getLeaderboardEntriesForWindow = (entries) => {
    return entries.slice(displayWindow[0], displayWindow[1])
  }

  useEffect(() => {
    const getLeaderboardData = async () => {
      // DEBUG: Create test leaderboard entries
      /*
      const leaderboard = {
        error: null,
        result: Array.from(new Array(42)).map((_, i) => ({
          player_name: `Player #${i + 1}`,
          score: Math.floor(Math.random() * 500),
          game_name: `Test game # ${i + 1}`,
          score_time: { _seconds: 1788194144 }
        }))
      }
      */
      const leaderboard = await getLeaderboardForVenue(params.venue_id)

      if (leaderboard) {
        entryRefs.current = leaderboard.result?.map((_, i) => entryRefs.current[i] ?? createRef())
        setLeaderboard(leaderboard)
      } else {
        setLeaderboard(null)
      }
    }
    const getVenueData = async () => {
      const venue = await getVenue(params.venue_id)
      if (venue) {
        setVenueData(venue)
        await getLeaderboardData()

        // Refresh periodically
        setInterval(getLeaderboardData, 60000)
      } else {
        setVenueData(null)
      }
    }
    if (params.venue_id) {
      getVenueData()
    }
  }, [params])

  useEffect(() => {
    if (searchParams.get('display_mode')) {
      switch (searchParams.get('display_mode').toString().toUpperCase()) {
        case 'TV':
          setDisplayMode('TV')
          break
      }
    }
    setGsb(searchParams.get('gsb') !== '0')
    setOneline(searchParams.get('oneline') === '1')
    setShowVenueLogo(searchParams.get('venue_logo') !== '0')
    if (searchParams.get('name')) {
      setCurrentPlayerName(searchParams.get('name'))
    } else {
      setCurrentPlayerName(null)
    }
    if (searchParams.get('score')) {
      setCurrentPlayerScore(parseInt(searchParams.get('score')))
    } else {
      setCurrentPlayerScore(null)
    }
  }, [searchParams])

  useEffect(() => {
    if (displayMode === 'TV') {
      const newDisplayWindow = resetDisplayWindow(0)
      displayWindowUpdateTimeout = setTimeout(
        () => updateDisplayWindow(leaderboard?.result, newDisplayWindow),
        TV_MODE_UPDATE_INTERVAL
      )
    }
    return () => {
      if (displayMode === 'TV') {
        if (displayWindowUpdateTimeout) {
          clearTimeout(displayWindowUpdateTimeout)
          displayWindowUpdateTimeout = null
        }
      }
    }
  }, [displayMode, leaderboard])

  useEffect(() => {
    const entries = (leaderboard?.result ?? [])
      .map((entry, index) => ({ ...entry, index }))
    if (displayMode === 'TV') {
      setLeaderboardDisplayEntries(
        getLeaderboardEntriesForWindow(entries)
      )
    } else {
      setLeaderboardDisplayEntries(entries)
    }
  }, [displayMode, displayWindow, leaderboard])

  useEffect(() => {
    setTimeout(scrollToEntry, 250)
  }, [currentPlayerName, currentPlayerScore, leaderboard])

  return (
    <Box
      style={{
        backgroundColor: 'rgba(255,255,255,0.8)',
        backgroundBlendMode: 'lighten',
        backgroundImage: venueData?.background_image ? `url(${venueData?.background_image})` : null,
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: '90%',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        maxWidth: 'auto',
        padding: '1rem 0'
      }}
    >
      {showVenueLogo && venueData && venueData.logo &&
        <Box sx={{ margin: 'auto' }}>
          <Box
            component="img"
            src={venueData.logo}
            alt={venueData.name}
            sx={{ width: '150px' }}
          ></Box>
        </Box>
      }
      <Box sx={{ margin: 'auto' }}>
        <h1 style={{ textTransform: 'uppercase' }}>
          {!showVenueLogo && venueData && (venueData.name || venueData.leaderboard_title)
            ? <span>{(venueData.leaderboard_title || venueData.name) + ' '}</span>
            : null}
          <span>Leaderboard</span>
        </h1>
      </Box>
        {leaderboardDisplayEntries && leaderboardDisplayEntries.length > 0 &&
        <ol style={{ margin: 0, padding: 0, minHeight: '100vh' }}>
          {leaderboardDisplayEntries.map((item, i) =>
            <LeaderboardEntry
              key={i}
              index={item.index}
              gsb={gsb}
              oneline={oneline}
            >
              <div
                ref={entryRefs.current[i]}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1
                }}>
                {oneline
                  ? <>
                      <div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1, alignItems: 'center' }}>
                        <LeaderboardPlayerName>{item.player_name}</LeaderboardPlayerName>
                        <LeaderboardGameAndTime
                          gameName={null}
                          scoreTime={item.score_time}
                        ></LeaderboardGameAndTime>
                        <LeaderboardScore>{Math.round(item.score)}</LeaderboardScore>
                      </div>
                    </>
                  : <>
                      <div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
                        <LeaderboardPlayerName>{item.player_name}</LeaderboardPlayerName>
                        <LeaderboardScore>{Math.round(item.score)}</LeaderboardScore>
                      </div>
                      <LeaderboardGameAndTime
                        gameName={item.game_name}
                        scoreTime={item.score_time}
                      ></LeaderboardGameAndTime>
                    </>}
              </div>
            </LeaderboardEntry>
          )}
        </ol>}
      {leaderboard && leaderboard.error && <Box sx={{ margin: 'auto' }}>{leaderboard.error}</Box>}
        {(!leaderboard ||
            (leaderboard &&
                !leaderboard.result) ||
            (leaderboard &&
                leaderboard.result.length === 0)) &&
            (!leaderboard ||
                (leaderboard &&
                    !leaderboard.error)) &&
          <Box sx={{ margin: 'auto', minHeight: '100vh' }}>No games played so far</Box>}
    </Box>
  )
}

export default LeaderboardPage
